import type {
  ColumnMapping,
  CsvImportMappableField,
  CsvImportModeOption,
  CsvImportWarning,
  CustomCsvPreviewResponse,
  CustomCsvWizardState,
  ImportMode,
  LayoutShape,
  ValueTransformMap,
  ValueTransforms,
  ImportDefaults,
} from "../types/customCsvImport";
import type { BrokerCapabilities } from "../services/brokerIntegrationApi";
import type { CustomCsvImportCapabilities } from "../services/brokerIntegrationApi";
import { mergeLayoutShapeIntoState } from "./layoutShapes";
import {
  CUSTOM_CSV_BROKER_DISPLAY_NAME,
  isCustomCsvBrokerName,
  resolveBrokerDisplayName,
  resolveCustomCsvTradeAccountLabels,
} from "./customCsvAccountDisplay";

export {
  mergeLayoutShapeIntoState,
  CUSTOM_CSV_BROKER_DISPLAY_NAME,
  isCustomCsvBrokerName,
  resolveBrokerDisplayName,
  resolveCustomCsvTradeAccountLabels,
};

const PROFILE_SLUG_RE = /^[a-z0-9_-]{1,64}$/;

export const CUSTOM_CSV_BROKER_SHORT_DESCRIPTION =
  "Your broker isn't in our list — upload a CSV and map the columns to Tessera.";

export function resolveCustomCsvCapability(
  cap: BrokerCapabilities | null | undefined
): CustomCsvImportCapabilities | null {
  if (!cap || !isCustomCsvBrokerName(cap.broker_name)) return null;
  const csv = cap.csv_import;
  if (!csv?.supported) return null;
  return csv as CustomCsvImportCapabilities;
}

export function validateProfileSlug(slug: string): boolean {
  return PROFILE_SLUG_RE.test(slug.trim());
}

export function isTypedTransform(value: unknown): value is { type: string } {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

export function stripTypedTransforms(transforms: ValueTransforms): ValueTransforms {
  const next: ValueTransforms = { ...transforms };
  for (const key of Object.keys(next)) {
    if (isTypedTransform(next[key])) {
      delete next[key];
    }
  }
  return next;
}

export function applyManualMappingMode(state: Pick<CustomCsvWizardState, "valueTransforms">): {
  valueTransforms: ValueTransforms;
} {
  return {
    valueTransforms: stripTypedTransforms(state.valueTransforms),
  };
}

export function mergeTemplateIntoState(
  template: {
    profile_slug: string;
    import_mode: ImportMode;
    layout_shape_id?: string | null;
    column_mapping: ColumnMapping;
    value_transforms: ValueTransforms;
    defaults: Record<string, unknown>;
    display_name?: string;
  }
): Partial<CustomCsvWizardState> {
  const defaults = (template.defaults ?? {}) as CustomCsvWizardState["defaults"];
  return {
    startSource: "profile",
    profileSlug: template.profile_slug,
    layoutShapeId: template.layout_shape_id ?? undefined,
    importMode: template.import_mode,
    columnMapping: { ...template.column_mapping },
    valueTransforms: { ...template.value_transforms },
    defaults: { ...defaults },
    displayName: template.display_name ?? "",
    quantityUnit: defaults.quantity_unit ?? "units",
    importInstrument: defaults.import_instrument ?? "",
    statementAccountCurrency: String(defaults.statement_account_currency ?? ""),
    contractMultipliers: { ...(defaults.contract_multipliers ?? {}) },
    timeZone: defaults.time_zone ?? "",
    preview: null,
    acknowledgedWarningCodes: new Set(),
  };
}

/** @deprecated Use mergeTemplateIntoState */
export const mergeProfileIntoState = mergeTemplateIntoState;

const CUSTOM_CSV_FALLBACK_OPTIONAL_FIELDS: Record<ImportMode, CsvImportMappableField[]> = {
  execution_rows: [
    { field: "realized_pnl", label: "Realized P&L (broker-reported, preferred)" },
    { field: "conversion_rate", label: "Conversion rate (FX cross fallback)" },
  ],
  closed_trade_paired: [
    { field: "net_pnl", label: "Net P&L (broker-reported, preferred)" },
    { field: "conversion_rate", label: "Conversion rate (FX cross fallback)" },
  ],
  closed_trade_single: [
    { field: "held_duration", label: "Hold duration" },
    { field: "gross_pnl", label: "Gross P&L (broker-reported, preferred)" },
    { field: "conversion_rate", label: "Conversion rate (FX cross fallback)" },
  ],
};

const CUSTOM_CSV_PNL_FIELD_LABELS: Record<string, string> = {
  realized_pnl: "Realized P&L (broker-reported, preferred)",
  net_pnl: "Net P&L (broker-reported, preferred)",
  gross_pnl: "Gross P&L (broker-reported, preferred)",
  conversion_rate: "Conversion rate (FX cross fallback)",
};

/** Clearer FE labels when capabilities copy is long / easy to misread. */
const CUSTOM_CSV_FIELD_LABEL_OVERRIDES: Record<string, string> = {
  exit_time: "Exit timestamp",
  held_duration: "Hold duration",
  entry_time: "Entry timestamp",
  entry_fee: "Entry fee / commission",
  exit_fee: "Exit fee / commission",
  fee: "Fee / commission",
};

/** Sentinel for clearing a column mapping in the picker. */
export const CUSTOM_CSV_UNMAP_COLUMN_VALUE = "__unmap__";

/** Ensure optional P&L / conversion fields appear even when capabilities omit them. */
export function enrichMappableFieldsForMode(
  cap: CustomCsvImportCapabilities | null,
  mode: ImportMode
): CsvImportMappableField[] {
  const fromCap = cap?.mappable_fields?.[mode];
  const fields = Array.isArray(fromCap) ? [...fromCap] : [];
  const fieldLabels = cap?.field_labels ?? {};
  const seen = new Set(fields.map((f) => f.field));

  for (const fallback of CUSTOM_CSV_FALLBACK_OPTIONAL_FIELDS[mode] ?? []) {
    if (seen.has(fallback.field)) continue;
    seen.add(fallback.field);
    fields.push(fallback);
  }

  return fields.map((field) => ({
    ...field,
    label:
      CUSTOM_CSV_FIELD_LABEL_OVERRIDES[field.field] ??
      fieldLabels[field.field] ??
      CUSTOM_CSV_PNL_FIELD_LABELS[field.field] ??
      field.label ??
      field.field.replace(/_/g, " "),
  }));
}

export function getMappableFieldsForMode(
  cap: CustomCsvImportCapabilities | null,
  mode: ImportMode
): CsvImportMappableField[] {
  return enrichMappableFieldsForMode(cap, mode);
}

export function buildCustomCsvDefaults(
  state: Pick<
    CustomCsvWizardState,
    "defaults" | "quantityUnit" | "importInstrument" | "statementAccountCurrency" | "contractMultipliers"
  >
): ImportDefaults {
  const contractMultipliers = Object.fromEntries(
    Object.entries(state.contractMultipliers).filter(
      ([symbol, value]) => symbol.trim() && Number.isFinite(value) && value > 0
    )
  );

  const built: ImportDefaults = {
    ...state.defaults,
    quantity_unit: state.quantityUnit,
  };

  const instrument = state.importInstrument.trim();
  if (instrument) built.import_instrument = instrument;

  const accountCurrency = state.statementAccountCurrency.trim().toUpperCase();
  if (accountCurrency) built.statement_account_currency = accountCurrency;

  if (Object.keys(contractMultipliers).length > 0) {
    built.contract_multipliers = contractMultipliers;
  }

  return built;
}

export function preferredPnlFieldForMode(mode: ImportMode): string {
  if (mode === "execution_rows") return "realized_pnl";
  if (mode === "closed_trade_paired") return "net_pnl";
  return "gross_pnl";
}

export function validateColumnMapping(
  mappableFields: CsvImportMappableField[],
  mapping: ColumnMapping,
  importInstrument?: string,
  requiredFields?: string[]
): string[] {
  const errors: string[] = [];
  const hasSymbol = Boolean(mapping.symbol?.trim());
  const instrument = (importInstrument ?? "").trim();
  const hasHeldDuration = Boolean(mapping.held_duration?.trim());
  const hasExitTime = Boolean(mapping.exit_time?.trim());

  const fieldsToRequire =
    requiredFields && requiredFields.length > 0
      ? requiredFields
      : mappableFields.filter((f) => f.required).map((f) => f.field);

  // Docs §5: if held_duration is mapped, exit_time is not required.
  const effectiveRequired = fieldsToRequire.filter(
    (fieldName) => !(hasHeldDuration && fieldName === "exit_time")
  );

  for (const fieldName of effectiveRequired) {
    if (fieldName === "symbol" && !hasSymbol && instrument) continue;
    if (!mapping[fieldName]?.trim()) {
      const label = mappableFields.find((f) => f.field === fieldName)?.label ?? fieldName;
      errors.push(`${label} is required`);
    }
  }

  if (
    fieldsToRequire.includes("symbol") &&
    !hasSymbol &&
    !instrument
  ) {
    errors.push("Map a symbol column or provide a single instrument for the whole file");
  }

  const supportsHeldExit =
    mappableFields.some((f) => f.field === "held_duration") &&
    mappableFields.some((f) => f.field === "entry_time" || f.field === "exit_time");
  if (supportsHeldExit && !hasExitTime && !hasHeldDuration) {
    const exitWasRequired =
      fieldsToRequire.includes("exit_time") ||
      Boolean(mappableFields.find((f) => f.field === "exit_time")?.required);
    if (exitWasRequired || fieldsToRequire.includes("entry_time")) {
      errors.push("Map exit timestamp or hold duration (Held)");
    }
  }

  return errors;
}

export function validateColumnMappingForShape(
  mappableFields: CsvImportMappableField[],
  mapping: ColumnMapping,
  shape: Pick<LayoutShape, "required_fields"> | undefined,
  importInstrument?: string
): string[] {
  return validateColumnMapping(
    mappableFields,
    mapping,
    importInstrument,
    shape?.required_fields
  );
}

/** Warn when a custom strptime format is missing required `%` prefixes. */
export function validateDatetimeFormat(format: string): string | null {
  const trimmed = format.trim();
  if (!trimmed) return null;
  if (!/%/.test(trimmed)) {
    return "Format codes must use % (e.g. %Y-%m-%d %H:%M:%S). You may have typed Y instead of %Y.";
  }
  if (/(^|[^%])[YmdHMS]/.test(trimmed)) {
    return "Each format code needs a leading % — use %Y not Y, %m not m, %d not d, %H not H, etc.";
  }
  return null;
}

function hasValueTransformMapEntries(map: ValueTransformMap | undefined): boolean {
  if (!map) return false;
  return Object.values(map).some((value) => String(value ?? "").trim() !== "");
}

/**
 * Normalize mapping/transforms before preview/import.
 * FIFO layout omits open/close column unless the user configured value mappings.
 */
export function buildCustomCsvImportPayload(input: {
  columnMapping: ColumnMapping;
  valueTransforms: ValueTransforms;
  layoutShapeId?: string;
}): { columnMapping: ColumnMapping; valueTransforms: ValueTransforms } {
  const columnMapping = { ...input.columnMapping };
  const valueTransforms = { ...input.valueTransforms };

  if (valueTransforms.datetime_format != null) {
    valueTransforms.datetime_format = String(valueTransforms.datetime_format).trim();
  }

  if (input.layoutShapeId === "fill_fifo") {
    const effectRaw = valueTransforms.position_effect;
    const effectMap =
      effectRaw && !isTypedTransform(effectRaw) && typeof effectRaw === "object"
        ? (effectRaw as ValueTransformMap)
        : undefined;
    if (!hasValueTransformMapEntries(effectMap)) {
      delete columnMapping.position_effect;
      delete valueTransforms.position_effect;
    }
  }

  return { columnMapping, valueTransforms };
}

export function mappingFingerprint(input: {
  importMode: ImportMode;
  columnMapping: ColumnMapping;
  valueTransforms: ValueTransforms;
  defaults: Record<string, unknown>;
  layoutShapeId?: string;
  profileSlug?: string;
  fileName?: string;
  fileSize?: number;
  fileLastModified?: number;
}): string {
  return JSON.stringify({
    importMode: input.importMode,
    columnMapping: input.columnMapping,
    valueTransforms: input.valueTransforms,
    defaults: input.defaults,
    layoutShapeId: input.layoutShapeId ?? null,
    profileSlug: input.profileSlug ?? null,
    fileName: input.fileName ?? null,
    fileSize: input.fileSize ?? null,
    fileLastModified: input.fileLastModified ?? null,
  });
}

export function buildAcknowledgedWarningCodes(
  warnings: CsvImportWarning[],
  checked: Set<string>
): string {
  return warnings
    .map((w) => w.code)
    .filter((code) => checked.has(code))
    .join(",");
}

export function findImportModeOption(
  modes: CsvImportModeOption[] | undefined,
  mode: ImportMode
): CsvImportModeOption | undefined {
  return modes?.find((m) => m.id === mode);
}

export function orderMappableFieldsForShape(
  mappableFields: CsvImportMappableField[],
  shape: Pick<LayoutShape, "highlight_fields"> | undefined
): CsvImportMappableField[] {
  if (!shape?.highlight_fields?.length) return mappableFields;
  const highlight = new Set(shape.highlight_fields);
  const highlighted = shape.highlight_fields
    .map((field) => mappableFields.find((f) => f.field === field))
    .filter(Boolean) as CsvImportMappableField[];
  const rest = mappableFields.filter((f) => !highlight.has(f.field));
  return [...highlighted, ...rest];
}

export function distinctColumnValues(
  sampleRows: Record<string, string>[],
  columnHeader: string,
  limit = 20
): string[] {
  const seen = new Set<string>();
  const values: string[] = [];
  for (const row of sampleRows) {
    const raw = row[columnHeader];
    if (raw == null) continue;
    const v = String(raw).trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    values.push(v);
    if (values.length >= limit) break;
  }
  return values;
}

const NON_TRADE_STATEMENT_VALUE_RE =
  /balance|deposit|withdraw|transfer|credit|dividend|fee|adjustment|interest|statement|financ|swap|rollover|funding|commission/i;

/** Rows like Deposit / Opening balance — not open-close trade labels. */
export function isIgnoredStatementRowValue(value: string): boolean {
  return NON_TRADE_STATEMENT_VALUE_RE.test(value.trim());
}

export type OpenCloseColumnPartition = {
  mappable: string[];
  ignored: string[];
};

/** Column headers that usually hold row/event categories, not open vs close of a position. */
export function isLikelyRowCategoryColumn(columnName: string): boolean {
  return /transaction\s*type|txn\s*type|event\s*type|row\s*type|activity\s*type/i.test(
    columnName.trim()
  );
}

/** Split column values into mappable open/close labels vs ignored statement rows. */
export function partitionOpenCloseColumnValues(
  allValues: string[],
  existingMapping: Record<string, string> = {}
): OpenCloseColumnPartition {
  const ignored = allValues.filter(isIgnoredStatementRowValue);
  const mappable = allValues.filter(
    (value) => !isIgnoredStatementRowValue(value) || Boolean(existingMapping[value]?.trim())
  );
  return { mappable, ignored };
}

/** File value mapped to a Tessera target (e.g. OPEN), for target-first mapping UIs. */
export function getSourceValueForTarget(
  mapping: Record<string, string>,
  target: string
): string {
  return Object.entries(mapping).find(([, mappedTarget]) => mappedTarget === target)?.[0] ?? "";
}

/** Update mapping when the user picks a file value for a fixed Tessera target. */
export function setSourceValueForTarget(
  mapping: Record<string, string>,
  target: string,
  source: string
): Record<string, string> {
  const next = { ...mapping };
  for (const [key, mappedTarget] of Object.entries(next)) {
    if (mappedTarget === target) delete next[key];
  }
  if (source) {
    delete next[source];
    next[source] = target;
  }
  return next;
}

export function sourceOptionsForTarget(
  distinctValues: string[],
  mapping: Record<string, string>,
  target: string
): string[] {
  const current = getSourceValueForTarget(mapping, target);
  const usedElsewhere = new Set(
    Object.entries(mapping)
      .filter(([, mappedTarget]) => mappedTarget !== target)
      .map(([source]) => source)
  );
  return distinctValues.filter((value) => !usedElsewhere.has(value) || value === current);
}

export function describeOpenCloseColumnIssue(columnName: string): string | null {
  if (!isLikelyRowCategoryColumn(columnName)) return null;
  return (
    `"${columnName}" usually lists row or event categories, not whether a trade is opening or closing. ` +
    "Map a column whose values distinguish open vs close fills, or choose a layout without an open/close column — Tessera will pair buys and sells in file order (first buy with first sell, and so on)."
  );
}

/** Keep sample rows for the formats UI; force a fresh preview before import. */
export function invalidatePreviewAfterConfigChange(
  preview: CustomCsvPreviewResponse | null
): CustomCsvPreviewResponse | null {
  if (!preview) return null;
  return {
    ...preview,
    can_import: false,
    preview_token: "",
  };
}

/** Strip leading formula chars for safe display (§14). */
export function sanitizeCsvCellDisplay(value: string): string {
  const trimmed = String(value ?? "");
  if (trimmed.startsWith("=") || trimmed.startsWith("+") || trimmed.startsWith("-") || trimmed.startsWith("@")) {
    return `'${trimmed}`;
  }
  return trimmed;
}

/** Option label for column picker — notes when a header is already mapped elsewhere. */
export function formatColumnPickerLabel(
  header: string,
  currentField: string,
  columnMapping: ColumnMapping,
  mappableFields: CsvImportMappableField[]
): string {
  const usedBy = mappableFields.filter(
    (f) => f.field !== currentField && columnMapping[f.field]?.trim() === header
  );
  if (!usedBy.length) return header;
  const names = usedBy.map((f) => f.label || f.field).join(", ");
  return `${header} (already mapped to ${names})`;
}

/**
 * Apply a column mapping change. Empty / unmap sentinel clears the field.
 * exit_time and held_duration are mutually exclusive.
 */
export function applyColumnMappingChange(
  mapping: ColumnMapping,
  field: string,
  header: string
): ColumnMapping {
  const next: ColumnMapping = { ...mapping };
  const trimmed = header.trim();
  if (!trimmed || trimmed === CUSTOM_CSV_UNMAP_COLUMN_VALUE) {
    delete next[field];
    return next;
  }
  next[field] = trimmed;
  if (field === "held_duration") {
    delete next.exit_time;
  } else if (field === "exit_time") {
    delete next.held_duration;
  }
  return next;
}

/** Delimiter for local CSV header sniffing (upload step only; server preview is authoritative). */
export type CsvHeaderDelimiter = "," | ";" | "\t";

function parseSepDirective(line: string): CsvHeaderDelimiter | null {
  const trimmed = line.trim();
  if (!trimmed.toLowerCase().startsWith("sep=")) return null;
  const value = trimmed.slice(4).trim();
  if (!value) return "\t";
  if (value === "," || value === ";" || value === "\t") return value;
  return "\t";
}

function detectDelimiterFromHeaderLine(line: string): CsvHeaderDelimiter {
  const tabCount = (line.match(/\t/g) ?? []).length;
  const semiCount = (line.match(/;/g) ?? []).length;
  const commaCount = (line.match(/,/g) ?? []).length;
  if (tabCount > 0 && tabCount >= semiCount && tabCount >= commaCount) return "\t";
  if (semiCount > 0 && semiCount > commaCount) return ";";
  return ",";
}

function parseCsvLine(line: string, delimiter: CsvHeaderDelimiter): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

type ParsedCsvTable = {
  headers: string[];
  rows: Record<string, string>[];
};

function parseCsvTableFromText(text: string, maxDataRows = 400): ParsedCsvTable {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (!lines.length) return { headers: [], rows: [] };

  let headerLineIndex = 0;
  let delimiter: CsvHeaderDelimiter | null = parseSepDirective(lines[0]);
  if (delimiter != null) {
    headerLineIndex = 1;
  }

  const headerLine = lines[headerLineIndex];
  if (!headerLine?.trim()) return { headers: [], rows: [] };

  if (!delimiter) {
    delimiter = detectDelimiterFromHeaderLine(headerLine);
  }

  const headers = parseCsvLine(headerLine, delimiter).map((h) => h.replace(/^"|"$/g, "").trim());
  const rows: Record<string, string>[] = [];

  for (let i = headerLineIndex + 1; i < lines.length && rows.length < maxDataRows; i += 1) {
    const cells = parseCsvLine(lines[i], delimiter).map((c) => c.replace(/^"|"$/g, "").trim());
    if (!cells.some(Boolean)) continue;
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      if (header) row[header] = cells[index] ?? "";
    });
    rows.push(row);
  }

  return { headers, rows };
}

/** Parse data rows from CSV text (for value-mapping dropdowns). */
export function readCsvSampleRowsFromText(text: string, maxDataRows = 400): Record<string, string>[] {
  return parseCsvTableFromText(text, maxDataRows).rows;
}

export async function readCsvSampleRowsFromFile(
  file: File,
  maxDataRows = 400
): Promise<Record<string, string>[]> {
  const text = await readBlobPrefixAsText(file);
  return readCsvSampleRowsFromText(text, maxDataRows);
}

/** Headers from an inspect preview row (trim empties at the trailing edge only). */
export function headersFromInspectRow(
  row: { cells?: string[] } | null | undefined
): string[] {
  const cells = row?.cells ?? [];
  let end = cells.length;
  while (end > 0 && !String(cells[end - 1] ?? "").trim()) end -= 1;
  return cells.slice(0, end).map((c) => String(c ?? "").trim());
}

/**
 * Column titles for the mapper — only the chosen header row (or suggested_headers).
 * Never union/pad cells from other preview_rows (data rows pollute with "0.00", etc.).
 */
export function mappingHeadersFromInspect(
  inspect: {
    suggested_header_row_index?: number;
    suggested_headers?: string[];
    preview_rows?: Array<{ row_index: number; cells?: string[] }>;
  } | null | undefined,
  selectedRowIndex: number
): string[] {
  if (!inspect) return [];
  const suggested = Math.max(0, Math.floor(inspect.suggested_header_row_index ?? 0));
  const selected = Math.max(0, Math.floor(selectedRowIndex));
  const raw =
    selected === suggested && Array.isArray(inspect.suggested_headers) && inspect.suggested_headers.length > 0
      ? inspect.suggested_headers
      : (inspect.preview_rows?.find((r) => r.row_index === selected)?.cells ?? []);
  return headersFromInspectRow({ cells: raw }).filter((h) => h.length > 0);
}

/** Parse header row from CSV text (skips Excel `sep=` preamble). */
export function readCsvHeadersFromText(text: string): string[] {
  return parseCsvTableFromText(text, 0).headers;
}

/** True when preview/import API mis-read Excel `sep=` preamble or wrong delimiter. */
export function isBrokenCsvPreviewHeaders(headers: string[] | undefined): boolean {
  if (!headers?.length) return false;
  return headers.some((header) => header.trim().toLowerCase().startsWith("sep="));
}

export function hasBrokenCsvSampleRows(
  sampleRows: Array<Record<string, unknown>> | undefined
): boolean {
  if (!sampleRows?.length) return false;
  return sampleRows.some((row) => Object.prototype.hasOwnProperty.call(row, "None"));
}

export function describeCsvPreviewParseIssue(
  preview: { headers?: string[]; sample_rows?: Array<Record<string, unknown>> },
  localHeaders?: string[]
): string | null {
  const headers = preview.headers ?? [];
  if (isBrokenCsvPreviewHeaders(headers) || hasBrokenCsvSampleRows(preview.sample_rows)) {
    return (
      "This file starts with an Excel sep= delimiter line (often semicolon-separated). " +
      "The server treated that line as a column header instead of skipping it, so columns like " +
      "Time and Reference were not found. Your column mapping is likely correct — re-upload after " +
      "removing the first sep= line, or re-export as plain CSV without the Excel preamble."
    );
  }

  if (
    localHeaders?.length &&
    headers.length &&
    localHeaders.length >= 3 &&
    headers.length < localHeaders.length / 2 &&
    !localHeaders.every((header) => headers.includes(header))
  ) {
    return (
      `This file was read locally as: ${localHeaders.join(", ")}. ` +
      `The server read: ${headers.filter(Boolean).join(", ") || "(none)"}. ` +
      "The column delimiter may be wrong on the server — try removing a leading sep= line or re-exporting the CSV."
    );
  }

  return null;
}

export function resolveCsvHeadersForMapping(
  fileHeaders: string[],
  previewHeaders?: string[]
): string[] {
  // Prefer inspect / selected header-row titles. preview.headers can include cell values.
  if (fileHeaders.length > 0) return fileHeaders;
  if (previewHeaders?.length && !isBrokenCsvPreviewHeaders(previewHeaders)) {
    return previewHeaders;
  }
  return fileHeaders;
}

async function readBlobPrefixAsText(blob: Blob, maxBytes = 64 * 1024): Promise<string> {
  const slice = blob.size > maxBytes ? blob.slice(0, maxBytes) : blob;
  if (typeof slice.text === "function") {
    return slice.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read CSV file"));
    reader.readAsText(slice);
  });
}

export async function readCsvHeadersFromFile(file: File): Promise<string[]> {
  const text = await readBlobPrefixAsText(file);
  return readCsvHeadersFromText(text);
}

export const DATETIME_FORMAT_CUSTOM_VALUE = "__custom__";

export type DatetimeFormatOption = {
  value: string;
  label: string;
};

/** Fallback presets when capabilities omit `datetime_format_options`. */
export const DATETIME_FORMAT_PRESETS: readonly DatetimeFormatOption[] = [
  // Year-first / ISO
  { value: "%Y-%m-%d %H:%M:%S", label: "2024-01-15 15:30:45 — year, month, day" },
  { value: "%Y-%m-%d %H:%M", label: "2024-01-15 15:30 — year, month, day (no seconds)" },
  { value: "%Y-%m-%dT%H:%M:%S", label: "2024-01-15T15:30:45 — ISO with T (not a space)" },
  { value: "%Y/%m/%d %H:%M:%S", label: "2024/01/15 15:30:45 — year/month/day" },
  {
    value: "%Y-%m-%d %H:%M:%S %Z",
    label: "2024-01-15 15:30:45 EEST — date, time, timezone abbreviation",
  },
  { value: "%Y%m%d %H:%M:%S", label: "20240115 15:30:45 — compact year-month-day" },
  // US (month/day/year)
  { value: "%m/%d/%Y %H:%M:%S", label: "01/15/2024 15:30:45 — month/day/year (US)" },
  { value: "%m/%d/%Y %H:%M", label: "01/15/2024 15:30 — month/day/year (US)" },
  { value: "%m-%d-%Y %H:%M:%S", label: "01-15-2024 15:30:45 — month-day-year (US)" },
  // EU (day/month/year)
  { value: "%d/%m/%Y %H:%M:%S", label: "15/01/2024 15:30:45 — day/month/year (EU)" },
  { value: "%d/%m/%Y %H:%M", label: "15/01/2024 15:30 — day/month/year (EU)" },
  { value: "%d-%m-%Y %H:%M:%S", label: "15-01-2024 15:30:45 — day-month-year (EU)" },
  { value: "%d.%m.%Y %H:%M:%S", label: "15.01.2024 15:30:45 — day.month.year (EU)" },
  // 2-digit year
  { value: "%m/%d/%y %H:%M:%S", label: "01/15/24 15:30:45 — month/day/2-digit year (US)" },
  { value: "%d/%m/%y %H:%M:%S", label: "15/01/24 15:30:45 — day/month/2-digit year (EU)" },
  { value: "%d.%m.%y %H:%M:%S", label: "15.01.24 15:30:45 — day.month.2-digit year (EU)" },
  { value: "%d-%m-%y %H:%M:%S", label: "15-01-24 15:30:45 — day-month-2-digit year (EU)" },
  // Date only
  { value: "%Y-%m-%d", label: "2024-01-15 — date only, year-month-day" },
  { value: "%m/%d/%Y", label: "01/15/2024 — date only, month/day/year (US)" },
  { value: "%d/%m/%Y", label: "15/01/2024 — date only, day/month/year (EU)" },
] as const;

const DATETIME_FORMAT_PRESET_BY_VALUE = new Map(
  DATETIME_FORMAT_PRESETS.map((preset) => [preset.value, preset.label])
);

function labelForDatetimeFormatOption(value: string, fromCapabilities: boolean): string {
  const presetLabel = DATETIME_FORMAT_PRESET_BY_VALUE.get(value);
  if (presetLabel) return presetLabel;
  if (fromCapabilities) return `${value} (recommended by server)`;
  return value;
}

function datetimeFormatGroup(value: string): number {
  const hasTime = /%[HMS]/.test(value);
  const twoDigitYear = /%y/.test(value) && !/%Y/.test(value);
  const yearFirst = value.trimStart().startsWith("%Y");
  const us = value.trimStart().startsWith("%m");
  const eu = value.trimStart().startsWith("%d");

  if (hasTime && yearFirst) return 10;
  if (hasTime && us && !twoDigitYear) return 20;
  if (hasTime && eu && !twoDigitYear) return 30;
  if (hasTime && us && twoDigitYear) return 40;
  if (hasTime && eu && twoDigitYear) return 50;
  if (!hasTime && yearFirst) return 60;
  if (!hasTime && us) return 70;
  if (!hasTime && eu) return 80;
  return 90;
}

function normalizeDatetimeFormatCapabilityOption(
  raw: unknown
): { value: string; label?: string } | null {
  if (typeof raw === "string") {
    const value = raw.trim();
    return value ? { value } : null;
  }
  if (raw && typeof raw === "object") {
    const o = raw as { value?: unknown; label?: unknown; format?: unknown };
    const value = String(o.value ?? o.format ?? "").trim();
    if (!value) return null;
    const label = typeof o.label === "string" && o.label.trim() ? o.label.trim() : undefined;
    return { value, label };
  }
  return null;
}

/** Presets plus any formats returned on `csv_import` capabilities (deduped). */
export function buildDatetimeFormatSelectOptions(
  csvImport?: Pick<
    CustomCsvImportCapabilities,
    "datetime_format_options" | "date_format_options"
  >
): DatetimeFormatOption[] {
  const fromCapabilities = [
    ...(csvImport?.datetime_format_options ?? []),
    ...(csvImport?.date_format_options ?? []),
  ]
    .map(normalizeDatetimeFormatCapabilityOption)
    .filter((option): option is { value: string; label?: string } => option != null);

  const capabilityValues = new Set(fromCapabilities.map((option) => option.value));
  const seen = new Set<string>();
  const options: DatetimeFormatOption[] = [];

  for (const preset of DATETIME_FORMAT_PRESETS) {
    if (seen.has(preset.value)) continue;
    seen.add(preset.value);
    options.push({ ...preset });
  }

  for (const option of fromCapabilities) {
    if (seen.has(option.value)) continue;
    seen.add(option.value);
    options.push({
      value: option.value,
      label:
        option.label ??
        labelForDatetimeFormatOption(option.value, capabilityValues.has(option.value)),
    });
  }

  options.sort((a, b) => {
    const groupA = datetimeFormatGroup(a.value);
    const groupB = datetimeFormatGroup(b.value);
    if (groupA !== groupB) return groupA - groupB;
    const presetA = DATETIME_FORMAT_PRESETS.findIndex((preset) => preset.value === a.value);
    const presetB = DATETIME_FORMAT_PRESETS.findIndex((preset) => preset.value === b.value);
    const orderA = presetA >= 0 ? presetA : 1000;
    const orderB = presetB >= 0 ? presetB : 1000;
    if (orderA !== orderB) return orderA - orderB;
    return a.value.localeCompare(b.value);
  });

  return options;
}

export function createInitialWizardState(
  partial?: Partial<CustomCsvWizardState>
): CustomCsvWizardState {
  return {
    startSource: "layout",
    importMode: "closed_trade_single",
    csvFile: null,
    headerRowIndex: 0,
    inspectResult: null,
    columnMapping: {},
    valueTransforms: {},
    defaults: {},
    displayName: "",
    accountName: "",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    tradedAssetClasses: [],
    quantityUnit: "units",
    importInstrument: "",
    statementAccountCurrency: "",
    contractMultipliers: {},
    preview: null,
    acknowledgedWarningCodes: new Set(),
    saveProfile: false,
    saveTemplate: false,
    ...partial,
  };
}
